import React from "react";
import { NodeEdge } from "@hex-insights/app-modules";
import {
	addTimeToDate,
	Button,
	ClassNameProps,
	Column,
	Conditional,
	dateTrunc,
	Heading,
	If,
	Row,
	StyleProps,
	Tooltip,
} from "@hex-insights/core";
import {
	DateTimeField,
	Form,
	FormState,
	FormTooltipButton,
	FormType,
	submissionSuccess,
	SubmitButton,
	useFormState,
	useOnSuccess,
	ValidationDisplayPolicy,
} from "@hex-insights/forms";
import { Icon } from "../../Icon";
import { CalendarItemGroup, EventCalendarItem, EventTooltipProps } from "../types";
import { BusinessWeekView, BusinessWeekViewProps } from "./BusinessWeekView";
import { DayView, DayViewProps } from "./DayView";
import { MonthView, MonthViewProps } from "./MonthView";
import { WeekView, WeekViewProps } from "./WeekView";

export type BigEventCalendarView = "month" | "week" | "day";

const defaultViews: BigEventCalendarView[] = ["month", "week", "day"];

export type BigEventCalendarProps = {
	value?: Date;
	onChange?: (value: Date, event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
	groups?: CalendarItemGroup[];
	events?: EventCalendarItem[] | NodeEdge<EventCalendarItem>[];
	detailModalVariant?: "default" | "admin";
	initialView?: BigEventCalendarView;
	views?: BigEventCalendarView[];
	noNavigation?: boolean;
	EventTooltip?: (props: EventTooltipProps) => JSX.Element | null;
	noItemIcon?: boolean;
} & Partial<ClassNameProps & StyleProps>;

export function BigEventCalendar({
	value = new Date(),
	onChange,
	groups,
	events,
	detailModalVariant = "default",
	initialView = "month",
	views = defaultViews,
	noNavigation = false,
	EventTooltip,
	noItemIcon,
	className,
	style,
}: BigEventCalendarProps) {
	const [view, baseSetView] = React.useState<BigEventCalendarView>(initialView);
	const setView = React.useCallback(
		(view: BigEventCalendarView) => {
			if (!views.includes(view)) {
				return;
			}
			baseSetView(view);
		},
		[views],
	);
	const { monthDate, weekDate, dayDate, onPrevClick, onNextClick, onTodayClick, setDate } = useCalendarNavigation(
		view,
		value,
	);

	const onClickDateFromMonthView = React.useCallback(
		(date: Date) => {
			setDate(date);
			setView("day");
		},
		[setDate, setView],
	);

	React.useEffect(() => {
		if (onChange) {
			onChange(monthDate, { preventDefault: () => {} } as React.MouseEvent<HTMLButtonElement, MouseEvent>);
		}
	}, [onChange, monthDate]);

	return (
		<Column justify="spaced-start" className={className} style={style}>
			<Row justify="space-between" align="center" style={{ paddingRight: "3rem" }}>
				<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
					<div style={{ fontSize: "1.5rem", fontWeight: "bold", width: "20rem" }}>
						<Conditional>
							<If condition={view === "month"}>
								{monthDate.toLocaleString(undefined, { year: "numeric", month: "long" })}
							</If>
							<If condition={view === "week"}>
								{dateTrunc(weekDate, "month").toDate().toLocaleString(undefined, { year: "numeric", month: "long" })}
							</If>
							<If condition={view === "day"}>
								{dayDate.toLocaleString(undefined, {
									year: "numeric",
									month: "short",
									day: "numeric",
									weekday: "long",
								})}
							</If>
						</Conditional>
					</div>

					<If condition={!noNavigation}>
						<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
							<Button variant="link" onClick={onPrevClick}>
								<Icon.CaretLeft size={16} className="block" />
							</Button>
							<Button variant="link" onClick={onNextClick}>
								<Icon.CaretRight size={16} className="block" />
							</Button>
							<Button variant="link" onClick={onTodayClick}>
								Today
							</Button>
							<JumpToButton setDate={setDate} />
						</Row>
					</If>
				</Row>
				<Row justify="spaced-start" align="center">
					<If condition={views.includes("month")}>
						<Button
							variant={view === "month" ? "primary" : "tertiary"}
							onClick={() => setView("month")}
							style={{ borderRadius: "10rem" }}
						>
							Month
						</Button>
					</If>
					<If condition={views.includes("week")}>
						<Button
							variant={view === "week" ? "primary" : "tertiary"}
							onClick={() => setView("week")}
							style={{ borderRadius: "10rem" }}
						>
							Week
						</Button>
					</If>
					<If condition={views.includes("day")}>
						<Button
							variant={view === "day" ? "primary" : "tertiary"}
							onClick={() => setView("day")}
							style={{ borderRadius: "10rem" }}
						>
							Day
						</Button>
					</If>
				</Row>
			</Row>

			<Row justify="spaced-start" style={{ flexGrow: 1, height: "100%", overflow: "hidden" }}>
				<If condition={!!groups && groups.length > 0}>
					<Column>
						{/* TODO */}
						<Heading.H5 noMargin>Calendars</Heading.H5>
						<Column>
							{groups?.map((group) => (
								<Row key={group.id} justify="spaced-start" align="center">
									<input type="checkbox" /> Meetings
								</Row>
							))}
						</Column>
					</Column>
				</If>

				<Conditional>
					<If condition={view === "month"}>
						<BigEventCalendar.MonthView
							value={monthDate}
							events={events}
							detailModalVariant={detailModalVariant}
							onClickDate={onClickDateFromMonthView}
							EventTooltip={EventTooltip}
							style={{ flexGrow: 1 }}
						/>
					</If>
					<If condition={view === "week"}>
						<BigEventCalendar.WeekView
							value={weekDate}
							events={events}
							detailModalVariant={detailModalVariant}
							EventTooltip={EventTooltip}
							noItemIcon={noItemIcon}
							style={{ flexGrow: 1 }}
						/>
					</If>
					<If condition={view === "day"}>
						<BigEventCalendar.DayView
							value={dayDate}
							events={events}
							detailModalVariant={detailModalVariant}
							EventTooltip={EventTooltip}
							noItemIcon={noItemIcon}
							style={{ flexGrow: 1 }}
						/>
					</If>
				</Conditional>
			</Row>
		</Column>
	);
}

type JumpToDateFormValues = {
	date: string | null;
};

const initialJumpToDateFormValues: JumpToDateFormValues = {
	date: null,
};

type JumpToButtonProps = {
	setDate: (date: Date) => void;
};

function JumpToButton({ setDate }: JumpToButtonProps) {
	const formState = useFormState({
		initialFormValues: initialJumpToDateFormValues,
		validationDisplayPolicy: ValidationDisplayPolicy.none,
		formType: FormType.Standing,
	});

	return (
		<FormTooltipButton formState={formState} autoFocus side="bottom">
			{({ formState, close }) => (
				<Tooltip isInteractable>
					<Tooltip.Body>
						<JumpToButtonForm formState={formState} setDate={setDate} close={close} />
					</Tooltip.Body>
				</Tooltip>
			)}
			{({ onClick, disabled }) => (
				<Button variant="link" onClick={onClick} disabled={disabled}>
					Jump To
				</Button>
			)}
		</FormTooltipButton>
	);
}

type JumpToButtonFormProps = {
	formState: FormState<JumpToDateFormValues>;
	setDate: (date: Date) => void;
	close: () => void;
};

function JumpToButtonForm({ formState, setDate, close }: JumpToButtonFormProps) {
	const onSubmit = React.useCallback(async () => {
		return submissionSuccess();
	}, []);

	useOnSuccess(formState, (formState) => {
		setDate(new Date(formState.formValues.date!));
		close();
		formState.clearFormValues();
	});

	return (
		<Form
			formState={formState}
			onSubmit={formState.onSubmitWrapper(onSubmit)}
			noNotifications
			noLocalStorage
			noNavigationPrompt
			noChangesDialog
			noCloseConfirmation
		>
			<Form.Column>
				<DateTimeField formState={formState} name="date" autoFocus />

				<Form.ActionBar style={{ zIndex: 1 }}>
					<SubmitButton onClick={formState.onSubmitWrapper(onSubmit)} submissionStatus={formState.submissionStatus}>
						Jump to Date
					</SubmitButton>
				</Form.ActionBar>
			</Form.Column>
		</Form>
	);
}

function useCalendarNavigation(view: BigEventCalendarView, value: Date) {
	const [monthDate, setMonthDate] = React.useState(() => dateTrunc(value, "month").toDate());
	const [weekDate, setWeekDate] = React.useState(() => dateTrunc(value, "week").toDate());
	const [dayDate, setDayDate] = React.useState(() => dateTrunc(value, "day").toDate());

	const onNavigationClick = React.useCallback(
		(direction: number) => {
			if (view === "month") {
				const nextMonthDate = addTimeToDate(monthDate, [direction, "month"]).toDate();
				setMonthDate(nextMonthDate);
				setWeekDate(dateTrunc(nextMonthDate, "week").toDate());
				setDayDate(dateTrunc(nextMonthDate, "day").toDate());
			} else if (view === "week") {
				const nextWeekDate = addTimeToDate(weekDate, [direction, "week"]).toDate();
				setMonthDate(dateTrunc(nextWeekDate, "month").toDate());
				setWeekDate(nextWeekDate);
				setDayDate(dateTrunc(nextWeekDate, "day").toDate());
			} else if (view === "day") {
				const nextDayDate = addTimeToDate(dayDate, [direction, "day"]).toDate();
				setMonthDate(dateTrunc(nextDayDate, "month").toDate());
				setWeekDate(dateTrunc(nextDayDate, "week").toDate());
				setDayDate(nextDayDate);
			}
		},
		[view, monthDate, weekDate, dayDate],
	);

	const onPrevClick = React.useCallback(() => {
		onNavigationClick(-1);
	}, [onNavigationClick]);

	const onNextClick = React.useCallback(() => {
		onNavigationClick(1);
	}, [onNavigationClick]);

	const setDate = React.useCallback((value: Date) => {
		setMonthDate(dateTrunc(value, "month").toDate());
		setWeekDate(dateTrunc(value, "week").toDate());
		setDayDate(dateTrunc(value, "day").toDate());
	}, []);

	const onTodayClick = React.useCallback(() => {
		setDate(new Date());
	}, [setDate]);

	return { monthDate, weekDate, dayDate, onPrevClick, onNextClick, onTodayClick, setDate };
}

export type BigEventCalendarMonthViewProps = MonthViewProps;

BigEventCalendar.MonthView = MonthView;

export type BigEventCalendarWeekViewProps = WeekViewProps;

BigEventCalendar.WeekView = WeekView;

export type BigEventCalendarBusinessWeekViewProps = BusinessWeekViewProps;

BigEventCalendar.BusinessWeekView = BusinessWeekView;

export type BigEventCalendarDayViewProps = DayViewProps;

BigEventCalendar.DayView = DayView;
